import React, { useState, useEffect, useCallback } from 'react';
import {
	ExternalLink,
	BookOpen,
	MapPin,
	ChevronRight,
	Bookmark,
} from 'lucide-react';
import Banner from '../../components/layout/Banner';
import PageTitle from '../../components/layout/PageTitle';
import { appFeatures } from '../../constants/features';
import { useMockUser } from '../../context/MockUserContext';
import {
	fetchFavoriteCareTips,
	unfavoriteCareTip,
} from '../../services/trackingApi';
import styles from './RessourcesPage.module.css';

const PAGE_SIZE = 20;

const RessourcesPage = ({ onLogoClick }) => {
	const { currentUserId } = useMockUser();

	// Favorite care tips (My Library) - one shown at a time, paginated by cursor
	const [favorites, setFavorites] = useState([]);
	const [favIndex, setFavIndex] = useState(0);
	const [favTotal, setFavTotal] = useState(0);
	const [nextCursor, setNextCursor] = useState(null);
	const [favLoading, setFavLoading] = useState(true);
	const [favLoadingMore, setFavLoadingMore] = useState(false);

	// Get the resources feature data for consistent styling
	const resourcesFeature = appFeatures.find(
		(feature) => feature.id === 'resources',
	);

	// Load the first page of favorites. Pass silent=true to refresh in the
	// background without showing the full loading state.
	const loadFavorites = useCallback(
		(silent = false) => {
			if (!currentUserId) return Promise.resolve();

			if (!silent) setFavLoading(true);
			return fetchFavoriteCareTips({ pageSize: PAGE_SIZE }, currentUserId)
				.then((res) => {
					const data = res.data ?? [];
					setFavorites(data);
					setFavTotal(res.total ?? data.length);
					setNextCursor(res.next_cursor ?? null);
					setFavIndex((i) =>
						i < data.length ? i : Math.max(0, data.length - 1),
					);
				})
				.catch((err) => {
					console.error('Failed to fetch favorite care tips:', err);
					setFavorites([]);
					setFavTotal(0);
					setNextCursor(null);
				})
				.finally(() => {
					if (!silent) setFavLoading(false);
				});
		},
		[currentUserId],
	);

	useEffect(() => {
		setFavIndex(0);
		loadFavorites();
	}, [loadFavorites]);

	// Remove a care tip from the library: unfavorite it, drop it immediately
	// for instant feedback, then silently refresh the list from the server.
	const handleUnfavorite = (log) => {
		setFavorites((prev) => prev.filter((f) => f.id !== log.id));
		setFavTotal((t) => Math.max(0, t - 1));
		setFavIndex((i) => {
			const newLen = favorites.length - 1;
			return newLen > 0 ? Math.min(i, newLen - 1) : 0;
		});

		unfavoriteCareTip(log.id, currentUserId)
			.then(() => loadFavorites(true))
			.catch((err) => {
				console.error('Failed to unfavorite care tip:', err);
				// Reload from the server to restore the true state
				loadFavorites(true);
			});
	};

	// Advance to the next favorite, loading the next page when needed and
	// wrapping back to the start once the whole list has been seen.
	const handleNextFavorite = () => {
		if (favorites.length === 0) return;

		// Still have loaded items ahead
		if (favIndex < favorites.length - 1) {
			setFavIndex(favIndex + 1);
			return;
		}

		// At the last loaded item: fetch the next page if there is one
		if (nextCursor != null && !favLoadingMore) {
			setFavLoadingMore(true);
			fetchFavoriteCareTips(
				{ cursor: nextCursor, pageSize: PAGE_SIZE },
				currentUserId,
			)
				.then((res) => {
					const more = res.data ?? [];
					setFavorites((prev) => [...prev, ...more]);
					if (res.total != null) setFavTotal(res.total);
					setNextCursor(res.next_cursor ?? null);
					if (more.length > 0) {
						setFavIndex((i) => i + 1);
					} else {
						setFavIndex(0);
					}
				})
				.catch((err) => {
					console.error('Failed to fetch more favorite care tips:', err);
				})
				.finally(() => setFavLoadingMore(false));
			return;
		}

		// No more pages: cycle back to the beginning
		setFavIndex(0);
	};

	// Format an ISO timestamp as a short, readable date (e.g. "Jul 4, 2026")
	const formatShownAt = (iso) => {
		if (!iso) return '';
		const date = new Date(iso);
		if (Number.isNaN(date.getTime())) return '';
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	};

	const currentFavorite = favorites[favIndex];

	return (
		<div className={styles.container}>
			{/* Banner with clickable logo */}
			<Banner onLogoClick={onLogoClick} />

			{/* Main content */}
			<div className={styles.content}>
				{/* Page Title */}
				<PageTitle
					icon={resourcesFeature.icon}
					title={resourcesFeature.title}
					color={resourcesFeature.color}
				/>

				{/* My Library of Care-tips Section */}
				<div className={styles.section}>
					<h3 className={styles.sectionTitle}>
						<BookOpen className={styles.sectionIcon} />
						My Library of Care-tips
					</h3>

					{favLoading ? (
						<div className={styles.emptyState}>
							<p className={styles.emptyDescription}>Loading your care tips…</p>
						</div>
					) : currentFavorite ? (
						<div className={styles.favoriteCard}>
							<div className={styles.careTipHeader}>
								<span className={styles.careTipGoal}>
									{currentFavorite.care_tip?.term?.name}
								</span>
								{/* Answers the "Save Tip" button this tip was kept with on the
								    Tracking page, down to the bookmark — outlined there for a tip
								    not yet kept, filled here for one that is. The word says what
								    the click does; a heart meaning "click to un-heart" is the one
								    control people hesitate over. */}
								<button
									type='button'
									className={styles.removeTipButton}
									onClick={() => handleUnfavorite(currentFavorite)}
								>
									<Bookmark size={18} fill='currentColor' />
									Remove Tip
								</button>
							</div>
							<div className={styles.careTipContent}>
								<p className={styles.careTipText}>
									{currentFavorite.care_tip?.message}
								</p>
							</div>
							{currentFavorite.care_tip?.resources?.length > 0 && (
								<ul className={styles.resourceList}>
									{currentFavorite.care_tip.resources.map((resource) => (
										<li key={resource.id}>
											<a
												className={styles.resourceLink}
												href={resource.url}
												target='_blank'
												rel='noopener noreferrer'
											>
												<ExternalLink className={styles.resourceLinkIcon} />
												<span>{resource.name}</span>
											</a>
										</li>
									))}
								</ul>
							)}
							<div className={styles.careTipFooter}>
								<span className={styles.careTipDate}>
									{formatShownAt(currentFavorite.shown_at)}
								</span>
								<div className={styles.careTipNav}>
									<span className={styles.careTipCounter}>
										{favIndex + 1} / {favTotal}
									</span>
									<button
										className={styles.nextTipButton}
										onClick={handleNextFavorite}
										disabled={favTotal <= 1 || favLoadingMore}
										title='Next care tip'
									>
										<ChevronRight className={styles.nextTipIcon} />
									</button>
								</div>
							</div>
						</div>
					) : (
						<div className={styles.emptyState}>
							<h4 className={styles.emptyTitle}>No Favorite Care Tips Yet</h4>
							<p className={styles.emptyDescription}>
								Tap the heart on a care tip to save it here for easy access.
							</p>
						</div>
					)}
				</div>

				{/* Care Finder Section */}
				<div className={styles.section}>
					<h3 className={styles.sectionTitle}>
						<MapPin className={styles.sectionIcon} />
						Care Finder
					</h3>

					<div className={styles.careFinderContent}>
						<p className={styles.careFinderDescription}>
							Find local support and services for Parkinson's care.
						</p>

						<a
							href='https://carefinder.parkinson.ca/'
							target='_blank'
							rel='noopener noreferrer'
							className={styles.careFinderLink}
						>
							<span>Visit Care Finder</span>
							<ExternalLink className={styles.externalLinkIcon} />
						</a>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RessourcesPage;
